import { action, observable } from 'mobx';
import { appConfig } from 'cd-eft/config/AppConfig';
import stores from 'cd-eft/models/Stores';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import MapView from '@arcgis/core/views/MapView';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import { LinearUnit, constructUgoLayer } from 'cd-eft/utils/structureLayerUtils';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { UgoFixtureTypeResponseDTO, UgoPipelineMaterialTypeResponseDTO } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { IUgoSummaryStation } from 'cd-eft/components/Design/UGO/UGOSummary/UGOSummaryVM';

//intial version and it need updates
interface IUgoDesignObject {
  alignedElementDesignParamsId: number;
  alignedFeatureName: string;
  alignmentDesignElementJsonId: number;
  backFillSoilType: string;
  benchmarkDescription: string;
  benchmarkElevation: number;
  benchmarkName: string;
  comments: string | null;
  constructBenchmark: string;
  designElementTypeId: number;
  sessionUuid: string;
  ugoDesignStations: IUgoVertexParamsDTO[];
  ugoPipeSpecification: IUgoPipeSpecification;
}

export interface IUgoVertexParamsDTO {
  reachId: string;
  status: string;
  startStation: string;
  endStation: string;
  groundElev: number;
  pipeFlowElev: number;
  grade: number;
  pipeFlowDepth: number;
  pipeCoverDepth: number;
  stationDist: number;
  manningN: number;
  isPressureFlow?: boolean;
  ugoPipeSpecification: IUgoPipeSpecification;
  runoffQ: number;
  fixtureTypeId: number;
  fixtureName: string | null;
  fixtureDtoJson: string | null;
  errorResponse: IErrorResponseDTO;
}

export interface IUgoPipeSpecification {
  materialTypeId: number | undefined;
  ugoMaterialDescription: string;
  pipelineSize: number;
  pipeManningn?: number;
  perforated?: boolean;
  pressurizable?: boolean;
  pressureFlow?: boolean;
  offsetDistance?: number;
  elbowElevation?: number;
}

interface IErrorResponseDTO {
  errorCode: string | null;
  errorMessage: string | null;
}

interface IUgoGroupTracker {
  groupCount: number;
  currentObject: IUgoDesignObject;
}

export interface IInputForms {
  UGOInfoSection: {
    ugoName: string;
    backFillSoilType: string;
    pipelineBenchmark: string;
    constructionBenchmark: string;
    benchmarkElevation: number;
    benchmarkDescription: string;
  };
  AlignmentSection: {
    alignment: number;
  };
  PipelineDefaultSection: {
    material: number;
    pressurizable?: boolean;
    pressureFlow?: boolean;
    pipeSize: number;
  };
}

export const initialUgoInputForm = {
  UGOInfoSection: {
    ugoName: '',
    backFillSoilType: '',
    pipelineBenchmark: '',
    constructionBenchmark: '',
    benchmarkElevation: 0,
    benchmarkDescription: '',
  },
  AlignmentSection: {
    alignment: 0,
  },
  PipelineDefaultSection: {
    material: 0,
    pressurizable: false,
    pressureFlow: false,
    pipeSize: 0,
  },
}

export interface IUgoProfileSettings {
  profileSettingPopUpActive: boolean;
  checked: {
    showFineGrid: boolean;
    previews: boolean;
  };
  input: {
    fineGrid: number;
    sampleInterval: number;
  };
}

export const ugoProfileSettingsDefault: IUgoProfileSettings = {
  profileSettingPopUpActive: false,
  checked: {
    showFineGrid: false,
    previews: false,
  },
  input: {
    fineGrid: Math.trunc(25.0),
    sampleInterval: 2.0,
  },
};
export class UGODesignStore {
  constructor(private appStore: any) {
    this.init();
  }

  @observable ugoDesignObject!: IUgoDesignObject;
  @observable ugoSidebarIsCollapsed: boolean = true;
  @observable displayResultBanner: boolean = false;
  @observable ugoGroupsOriginals: IUgoGroupTracker[] = []; // Used to save pre-PEM UGO groups
  @observable hasCalculatedData: boolean = true;
  @observable deleteStationFromTable: boolean = false;
  @observable ugoHistory: any
  @observable ugoInputsChanged: boolean = false;
  @observable ugoLayers: GroupLayer[] = [];
  @observable notifications = {
    unsavedEditsClosePage: ''
  }
  @observable savedProfileSettingsForm: IUgoProfileSettings = ugoProfileSettingsDefault;
  @observable pipelineMaterials: Map<number, UgoPipelineMaterialTypeResponseDTO> = new Map();
  @observable fixtureTypes: Map<number, UgoFixtureTypeResponseDTO> = new Map();
  @observable pipelineMaterialOptions: { name: string, value: number }[] = [];
  @observable mannningsNJson: { pipeSize: number, manningsN: number }[] = [];
  @observable fixtureTypeOptions: { name: string, value: number }[] = [];
  @observable simulateRunoffIsOpen: boolean = false;
  @observable ugoSummaryList: IUgoSummaryStation[] = [];
  @observable ugoSummaryStations: any[] = [];

  @observable.deep inputForms: IInputForms = initialUgoInputForm;

  @action.bound onClickSimulateRunoffToggle = () => {
    this.simulateRunoffIsOpen = !this.simulateRunoffIsOpen
  }

  initialUgoDesignObject: IUgoDesignObject = {
    alignedElementDesignParamsId: 0,
    alignedFeatureName: '',
    alignmentDesignElementJsonId: 0,
    backFillSoilType: '',
    benchmarkDescription: '',
    benchmarkElevation: 0,
    benchmarkName: '',
    comments: '',
    constructBenchmark: '',
    designElementTypeId: appConfig.hardCodes.designElementTypeUGO,
    sessionUuid: '',
    ugoPipeSpecification: {
      materialTypeId: 0,
      ugoMaterialDescription: '',
      pipelineSize: 0,
      perforated: false,
      pressurizable: false,
    },
    ugoDesignStations: []
  };

  initialUgoDesignStation: IUgoVertexParamsDTO = {
    reachId: '',
    status: '',
    startStation: '',
    endStation: '',
    groundElev: 0,
    pipeFlowElev: 0,
    grade: 0,
    pipeFlowDepth: 0,
    pipeCoverDepth: 0,
    stationDist: 0,
    manningN: 0,
    isPressureFlow: false,
    ugoPipeSpecification: {
      materialTypeId: 0,
      ugoMaterialDescription: '',
      pipelineSize: 0,
      perforated: false,
      pressurizable: false,
    },
    runoffQ: 0,
    fixtureTypeId: 0,
    fixtureName: '',
    fixtureDtoJson: null,
    errorResponse: {
      errorCode: null,
      errorMessage: null,
    },
  }

  @action.bound setUgoSideBarCollapsed = () => (this.ugoSidebarIsCollapsed = !this.ugoSidebarIsCollapsed);

  @action.bound closeAction() {
    this.ugoHistory?.push(`/design/${stores.AppStore.designStore.projectsStore.currentProjectId}/ugo`);
  }

  @action.bound handlecloseAction() {
    if (this.ugoInputsChanged) {
      MessageCenter.addWarning(stores.AppStore.ugoDesignStore.notifications?.unsavedEditsClosePage, [
        {
          callback: () => {
            MessageCenter.clearMessages();
          },
          text: 'Cancel',
        },
        {
          callback: async () => {
            MessageCenter.clearMessages();
            stores.AppStore.ugoDesignStore.reset()
            // Use a delay to allow state changes before redirecting
            setTimeout(() => {
              this.closeAction();
            }, 0);
          },
          text: 'OK',
        },
      ], 'Common_13');
    } else {
      MessageCenter.clearMessages();
      stores.AppStore.ugoDesignStore.reset()
      this.closeAction();
    }
  }

  @action resetCurrentObject() {
    this.ugoDesignObject = { ...this.initialUgoDesignObject };
  }
  @action.bound init() {
    this.resetCurrentObject();
    this.ugoSidebarIsCollapsed = true;
    this.displayResultBanner = false;
    this.hasCalculatedData = true;
    this.ugoInputsChanged = false;
    this.inputForms = initialUgoInputForm;
    // this.ugoSummaryList = [];
    // this.ugoSummaryStations = [];
  }

  @action.bound reset() {
    this.init();
  }

  @action addUGOToMap(ugo: any, mapView: MapView, id: number, title: string, fromRefresh: boolean = false): GroupLayer {
    const groupLayerId = `Ugo${id}`;
    const deleteLayer = this.ugoLayers.find(lyr => lyr.id === groupLayerId);
    if (deleteLayer && !fromRefresh) {
      mapView.map.remove(deleteLayer);
      deleteLayer.destroy();
      this.ugoLayers = this.ugoLayers.filter(lyr => lyr.id !== groupLayerId);
    }

    const newLayer = this.createUgoLayer(ugo, mapView, groupLayerId, title, id);
    mapView.map.add(newLayer);
    this.ugoLayers.push(newLayer);
    return newLayer;
  }

  private createUgoLayer(ugo: any, mapView: MapView, groupLayerId: string, title: string, alignedElementId): GroupLayer {
    const inputSR: SpatialReference = stores.AppStore.projectsLibraryStore.projectSpatialReference!;
    const outputSR: SpatialReference = mapView.spatialReference;
    const inputUnit: LinearUnit = 'intl-feet';
    const outputUnit: LinearUnit = 'meters';
    const ugoLayer = constructUgoLayer(ugo, inputSR, outputSR, inputUnit, outputUnit, groupLayerId, title, alignedElementId);
    return ugoLayer;
  }

  @action.bound validatePipeSize = (pipeSize: number, materialId: number, section: string, sizeFieldName: string, materialFieldName: string, formsValidation, redHighlighting, blockErrorCode = false) => {
    const selectedMaterial = this.pipelineMaterials.get(materialId);
    if (!selectedMaterial || selectedMaterial.minSize == null || selectedMaterial.maxSize == null) {
      return false;
    }
    const { minSize, maxSize } = selectedMaterial;
    const isValid = Number(pipeSize) >= minSize && Number(pipeSize) <= maxSize;
    if (isValid) {
      this.clearPipeErrors(section, sizeFieldName, materialFieldName, formsValidation, redHighlighting);
    } else if (!isValid && !blockErrorCode) {
      const button = [
        {
          text: 'Material list',
          callback: () => {
            window.open('/ugomateriallist', '_blank', 'noreferrer');
          },
        },
      ];
      MessageCenter.addErrorFromCode('UGO_04', button);
    }

    return isValid;
  }

  @action clearPipeErrors(section, pipeSizeName, pipeMaterialName, formsValidation, redHighlighting) {
    formsValidation[section][pipeSizeName] = true;
    formsValidation[section][pipeMaterialName] = true;
    redHighlighting[section][pipeSizeName] = false;
    redHighlighting[section][pipeMaterialName] = false;
  }
}
